import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Save } from 'lucide-react'
import { type FormEvent, useEffect, useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  SelectSearch,
  type SelectSearchOption,
} from '@/components/ui/select-search'
import { Spinner } from '@/components/ui/spinner'
import { Textarea } from '@/components/ui/textarea'
import { useApi } from '@/hooks/use-api'
import { ApiError } from '@/lib/api'

import type {
  ClientLite,
  DriverLite,
  PlanningRow,
  VehicleLite,
} from './planning-types'

/**
 * Create-or-edit planning dialog.
 *
 * Two modes, controlled by the `planning` prop:
 *   - **Create** (`planning === null`): blank form, default
 *     date = today, POST on submit.
 *   - **Edit** (`planning !== undefined && planning !== null`):
 *     pre-populated form, PATCH on submit.
 *
 * Both modes invalidate the `['planning', …]` cache on success
 * so the parent list refreshes without manual fiddling. We keep
 * the dialog OPEN until the network call finishes — closing
 * immediately would let the driver see a stale list for ~200 ms,
 * which feels janky on a desktop where rendering is otherwise
 * instant.
 *
 * Validation: `site` and `driverId` and `date` are required;
 * everything else is optional. Errors are inlined under each
 * field rather than alerted at the top because admin users
 * complete this form repeatedly and don't want to scroll.
 */

interface PlanningDialogProps {
  /**
   * - `null`     → create mode
   * - `PlanningRow` → edit mode, pre-populated from this row
   * - `undefined`   → dialog closed
   */
  planning: PlanningRow | null | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
  drivers: readonly DriverLite[]
  vehicles: readonly VehicleLite[]
  clients: readonly ClientLite[]
}

interface FormState {
  driverId: string | null
  vehicleId: string | null
  clientId: string | null
  site: string
  date: string
  startTime: string
  endTime: string
  isDaily: boolean
  details: string
}

interface FormErrors {
  driverId?: string
  site?: string
  date?: string
  time?: string
}

const EMPTY_FORM: FormState = {
  driverId: null,
  vehicleId: null,
  clientId: null,
  site: '',
  date: today(),
  startTime: '',
  endTime: '',
  isDaily: false,
  details: '',
}

export function PlanningDialog({
  planning,
  open,
  onOpenChange,
  drivers,
  vehicles,
  clients,
}: PlanningDialogProps) {
  const api = useApi()
  const queryClient = useQueryClient()

  const titleId = useId()
  const driverFieldId = useId()
  const vehicleFieldId = useId()
  const clientFieldId = useId()
  const siteFieldId = useId()
  const dateFieldId = useId()
  const detailsFieldId = useId()

  const isEdit = planning !== undefined && planning !== null

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [errors, setErrors] = useState<FormErrors>({})
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Reset/populate the form whenever the dialog (re)opens. We
  // depend on `open` so re-rendering the parent doesn't reset the
  // user's edits mid-typing.
  useEffect(() => {
    if (!open) return
    if (planning !== undefined && planning !== null) {
      setForm({
        driverId: planning.driverId,
        vehicleId: planning.vehicleId,
        clientId: planning.clientId,
        site: planning.site,
        date: planning.date,
        startTime: trimSeconds(planning.startTime ?? ''),
        endTime: trimSeconds(planning.endTime ?? ''),
        isDaily: planning.isDaily,
        details: planning.details ?? '',
      })
    } else {
      setForm(EMPTY_FORM)
    }
    setErrors({})
    setSubmitError(null)
  }, [open, planning])

  /* -------- mutations -------- */

  const saveMutation = useMutation({
    mutationFn: async (): Promise<void> => {
      const payload = buildPayload(form)
      if (isEdit) {
        await api.patch<unknown>(`/api/planning/${planning.id}`, payload)
      } else {
        await api.post<unknown>('/api/planning', payload)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['planning'] })
      onOpenChange(false)
    },
    onError: (err) => {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible d'enregistrer le planning.",
      )
    },
  })

  /* -------- handlers -------- */

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    const validation = validate(form)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    setSubmitError(null)
    saveMutation.mutate()
  }

  /* -------- options for selects -------- */

  const driverOptions: SelectSearchOption[] = drivers.map((d) => {
    const opt: SelectSearchOption = { value: d.id, label: d.name }
    if (d.email !== '') opt.detail = d.email
    return opt
  })

  const vehicleOptions: SelectSearchOption[] = vehicles
    .filter((v) => v.active)
    .map((v) => {
      const detail = [v.brand, v.model, v.type]
        .filter((s): s is string => s !== null && s !== '')
        .join(' · ')
      const opt: SelectSearchOption = { value: v.id, label: v.registration }
      if (detail !== '') opt.detail = detail
      return opt
    })

  const clientOptions: SelectSearchOption[] = clients.map((c) => {
    const opt: SelectSearchOption = { value: c.id, label: c.name }
    if (c.email !== null && c.email !== '') opt.detail = c.email
    return opt
  })

  /* -------- render -------- */

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" aria-labelledby={titleId}>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle id={titleId}>
              {isEdit ? 'Modifier le planning' : 'Nouveau planning'}
            </DialogTitle>
            <DialogDescription>
              {isEdit
                ? 'Modifiez les informations puis enregistrez.'
                : 'Renseignez les informations du planning.'}
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Date */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={dateFieldId}>Date *</Label>
              <Input
                id={dateFieldId}
                type="date"
                required
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                invalid={errors.date !== undefined}
              />
              {errors.date !== undefined ? (
                <p className="text-xs text-danger">{errors.date}</p>
              ) : null}
            </div>

            {/* Driver */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={driverFieldId}>Chauffeur *</Label>
              <SelectSearch
                triggerId={driverFieldId}
                placeholder="Sélectionner un chauffeur"
                value={form.driverId}
                onChange={(v) => setForm({ ...form, driverId: v })}
                options={driverOptions}
                allowClear={false}
                invalid={errors.driverId !== undefined}
              />
              {errors.driverId !== undefined ? (
                <p className="text-xs text-danger">{errors.driverId}</p>
              ) : null}
            </div>

            {/* Vehicle */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={vehicleFieldId}>Véhicule</Label>
              <SelectSearch
                triggerId={vehicleFieldId}
                placeholder="Optionnel"
                value={form.vehicleId}
                onChange={(v) => setForm({ ...form, vehicleId: v })}
                options={vehicleOptions}
              />
            </div>

            {/* Client */}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={clientFieldId}>Client</Label>
              <SelectSearch
                triggerId={clientFieldId}
                placeholder="Optionnel"
                value={form.clientId}
                onChange={(v) => setForm({ ...form, clientId: v })}
                options={clientOptions}
              />
            </div>

            {/* Site (full width) */}
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor={siteFieldId}>Chantier *</Label>
              <Input
                id={siteFieldId}
                type="text"
                required
                value={form.site}
                onChange={(e) => setForm({ ...form, site: e.target.value })}
                invalid={errors.site !== undefined}
                placeholder="Nom du chantier"
              />
              {errors.site !== undefined ? (
                <p className="text-xs text-danger">{errors.site}</p>
              ) : null}
            </div>

            {/* Times */}
            <div className="flex flex-col gap-1.5">
              <Label>Horaire début</Label>
              <Input
                type="time"
                step={60}
                value={form.startTime}
                onChange={(e) =>
                  setForm({ ...form, startTime: e.target.value })
                }
                disabled={form.isDaily}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Horaire fin</Label>
              <Input
                type="time"
                step={60}
                value={form.endTime}
                onChange={(e) =>
                  setForm({ ...form, endTime: e.target.value })
                }
                disabled={form.isDaily}
                invalid={errors.time !== undefined}
              />
              {errors.time !== undefined ? (
                <p className="text-xs text-danger">{errors.time}</p>
              ) : null}
            </div>

            {/* Daily flag */}
            <div className="sm:col-span-2 flex items-center gap-3">
              <input
                id="planning-daily"
                type="checkbox"
                checked={form.isDaily}
                onChange={(e) =>
                  setForm({
                    ...form,
                    isDaily: e.target.checked,
                    // Clear hours when switching to daily — they'd
                    // be hidden anyway and we don't want stale
                    // values lingering server-side.
                    ...(e.target.checked ? { startTime: '', endTime: '' } : {}),
                  })
                }
                className="size-4 rounded border-2 border-surface-border text-brand"
              />
              <label htmlFor="planning-daily" className="text-sm cursor-pointer">
                Mission journalière (forfait, pas d'horaires)
              </label>
            </div>

            {/* Details (full width) */}
            <div className="sm:col-span-2 flex flex-col gap-1.5">
              <Label htmlFor={detailsFieldId}>Notes</Label>
              <Textarea
                id={detailsFieldId}
                value={form.details}
                onChange={(e) =>
                  setForm({ ...form, details: e.target.value })
                }
                placeholder="Informations complémentaires (optionnel)"
              />
            </div>

            {submitError !== null ? (
              <p
                role="alert"
                className="sm:col-span-2 text-sm text-danger text-center font-medium"
              >
                {submitError}
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Annuler
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? (
                <Spinner label="Enregistrement" />
              ) : (
                <>
                  <Save size={16} />
                  {isEdit ? 'Enregistrer' : 'Créer'}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/u
const HH_MM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/u

function validate(form: FormState): FormErrors {
  const errors: FormErrors = {}
  if (form.driverId === null) errors.driverId = 'Chauffeur obligatoire.'
  if (form.site.trim() === '') errors.site = 'Nom du chantier obligatoire.'
  if (!ISO_DATE_REGEX.test(form.date)) errors.date = 'Date invalide.'

  // Times only constrained when both are filled and the mission is
  // not a daily forfait — partial input is allowed (V1 too).
  if (!form.isDaily && form.startTime !== '' && form.endTime !== '') {
    if (
      !HH_MM_REGEX.test(form.startTime) ||
      !HH_MM_REGEX.test(form.endTime)
    ) {
      errors.time = 'Format HH:MM requis.'
    }
  }
  return errors
}

interface PlanningPayload {
  driverId: string
  site: string
  date: string
  isDaily: boolean
  vehicleId?: string | null
  clientId?: string | null
  startTime?: string | null
  endTime?: string | null
  details?: string | null
}

function buildPayload(form: FormState): PlanningPayload {
  // `driverId` is guaranteed non-null at this point (validation
  // gate). Asserting via `as string` keeps the types clean.
  const payload: PlanningPayload = {
    driverId: form.driverId as string,
    site: form.site.trim(),
    date: form.date,
    isDaily: form.isDaily,
    // Send null explicitly when the user cleared an optional FK
    // — the API treats missing keys differently (no-op on PATCH).
    vehicleId: form.vehicleId,
    clientId: form.clientId,
    startTime: form.isDaily || form.startTime === '' ? null : form.startTime,
    endTime: form.isDaily || form.endTime === '' ? null : form.endTime,
    details: form.details.trim() === '' ? null : form.details.trim(),
  }
  return payload
}

function today(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function trimSeconds(value: string): string {
  // API stores HH:MM:SS for times; the input shows HH:MM.
  return value.length === 8 ? value.slice(0, 5) : value
}
